import { Injectable, inject } from '@angular/core';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, finalize, share } from 'rxjs';

/**
 * Coordinates token refreshes so that a burst of concurrent 401s triggers ONE
 * refresh instead of one per in-flight request.
 *
 * This deliberately holds the in-flight observable on an injectable instance
 * rather than in a module-level variable: module state is shared by every
 * injector (tests included) and cannot be reset, which made the previous
 * implementation order-dependent.
 */
@Injectable()
export class TokenRefreshService {
  private readonly oidc = inject(OidcSecurityService);

  private inFlight$: Observable<LoginResponse> | null = null;

  /**
   * Returns the in-flight refresh if one is already running, otherwise starts a
   * new one. The slot is cleared as soon as the refresh settles — completed,
   * errored, or abandoned by every subscriber — so the next 401 starts fresh.
   */
  refresh(): Observable<LoginResponse> {
    if (!this.inFlight$) {
      this.inFlight$ = this.oidc.forceRefreshSession().pipe(
        finalize(() => {
          this.inFlight$ = null;
        }),
        share(),
      );
    }

    return this.inFlight$;
  }
}
